import { Alert, AlertContent, ArrowForwardIcon } from '@tlx/atlas';
import {
    OnboardingButtonPrevious,
    OnboardingStageContent,
    OnboardingStageFooter,
    OnboardingStageHeader,
    OnboardingStageText,
    OnboardingStageTitle,
    StageState,
    useOnboardingContext,
    useOnboardingStageState,
} from '@tlx/beehive-shared';
import React, { Dispatch, SetStateAction, useId, useRef } from 'react';
import { useValidatedForm } from '@Page/Onboarding/components/ValidatedForm/hooks/useValidatedForm';
import { APIError, getContextId, InlineMarkdown, useSearchParam, ValidationMessage } from '@tlx/astro-shared';
import { SalaryOnboardingStep } from '@Page/Onboarding/hooks/useSalaryOnboardingPageOptions';
import { useSalaryOnboardingPageOptionsContext } from '@Page/Onboarding/SalaryOnboardingWithTips';
import { FormattedMessage } from 'react-intl';
import { SpinnerButton } from '@tlx/tsk-shared';

export function SalaryOnboardingStageForm<T>({
    title,
    description,
    handleSubmit,
    handleError,
    children,
}: {
    handleSubmit?: (formData: Partial<T>) => Promise<void> | void;
    handleError?: (form: HTMLFormElement, error: APIError) => ValidationMessage[] | void;
    title: string;
    description: string;
    children: React.ReactNode;
}) {
    const formId = useId();
    const formRef = useRef<HTMLFormElement>(null);
    const { formIsValid, formProps } = useValidatedForm({
        ref: formRef,
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const { activateNextStage, activeStage } = useOnboardingContext();
    const { setStepCompleted } = useSalaryOnboardingPageOptionsContext();
    const [generalErrors, setGeneralErrors] = React.useState<ValidationMessage[]>([]);

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        if (!form.checkValidity()) {
            return;
        }

        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        try {
            setGeneralErrors([]);
            setIsSubmitting(true);
            await handleSubmit?.(data as Partial<T>);
            setStepCompleted(activeStage as SalaryOnboardingStep);
            activateNextStage(StageState.Done);
        } catch (error) {
            if (error instanceof APIError) {
                const unhandledValidationMessages = handleError
                    ? handleError(form, error)
                    : error.details.validationMessages;
                if (unhandledValidationMessages) {
                    setGeneralErrors(unhandledValidationMessages);
                }
                form.checkValidity();
            } else {
                throw error;
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <>
            <OnboardingStageHeader className="atl-flex atl-flex-col atl-gap-12">
                <OnboardingStageTitle>{title}</OnboardingStageTitle>
                <OnboardingStageText>
                    <InlineMarkdown>{description}</InlineMarkdown>
                </OnboardingStageText>
            </OnboardingStageHeader>

            <form
                autoComplete="off"
                data-lpignore
                data-1pignore
                {...formProps}
                onSubmit={onSubmit}
                id={formId}
                noValidate
                className="salary-onboarding-form"
            >
                <OnboardingStageContent className="atl-flex atl-flex-col atl-gap-24">
                    {generalErrors.length > 0 && (
                        <Alert variant="error" className="atl-mb-16">
                            <AlertContent>
                                <ul className="atl-m-0">
                                    {generalErrors.map((message) => (
                                        <li key={`${message.field}-${message.message}`}>
                                            <span className="atl-font-medium">{message.field}: </span>
                                            <InlineMarkdown>{message.message}</InlineMarkdown>
                                        </li>
                                    ))}
                                </ul>
                            </AlertContent>
                        </Alert>
                    )}
                    {/*Mismatch between children type in TSK and beehive-shared*/}
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {children as any}
                </OnboardingStageContent>
            </form>
            <SalaryOnboardingFooter
                formId={formId}
                isValid={formIsValid}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
            />
        </>
    );
}

export function SalaryOnboardingFooter({
    formId,
    isValid = true,
    isSubmitting = false,
    setIsSubmitting,
}: {
    formId: string;
    isValid: boolean;
    isSubmitting: boolean;
    setIsSubmitting: Dispatch<SetStateAction<boolean>>;
}) {
    const { isFirstStage, isLastStage } = useOnboardingStageState();
    const { activeStage } = useOnboardingContext();
    const { setOnboardingCompleted } = useSalaryOnboardingPageOptionsContext();
    const redirectRoute = useSearchParam('redirect', '');
    const contextId = getContextId() ?? '';

    return (
        <OnboardingStageFooter>
            {!isFirstStage && (
                <OnboardingButtonPrevious
                    data-testid="salary-onboarding-button-previous"
                    data-trackingid={`salary-onboarding-button-previous-${activeStage}`}
                    onClick={() => {
                        // This doesn't work for now as you need to deactivate the "current step" after going to previous step.
                        // But we can't look at onMount because evrything is mounted immediately.
                        //setStepIncomplete(activeStage as SalaryOnboardingStep)
                    }}
                />
            )}
            <span className="atl-ml-auto">
                {/* {isFirstStage && (
                    <OnboardingButtonGetStarted
                        data-testid="salary-onboarding-button-get-started"
                        onClick={() =>
                            setStepCompleted(
                                activeStage as SalaryOnboardingStep,
                            )
                        }
                    />
                )} */}
                {!isLastStage && (
                    <SpinnerButton
                        form={formId}
                        disabled={!isValid}
                        type="submit"
                        data-testid="salary-onboarding-button-next"
                        data-trackingid={`salary-onboarding-button-next-${activeStage}`}
                        isLoading={isSubmitting}
                    >
                        <FormattedMessage id="text_next" />
                        <ArrowForwardIcon />
                    </SpinnerButton>
                )}
                {isLastStage && (
                    <SpinnerButton
                        variant="primary"
                        data-testid="salary-onboarding-button-get-started"
                        data-trackingid={`salary-onboarding-button-get-started-${activeStage}`}
                        onClick={async () => {
                            setIsSubmitting(true);
                            await setOnboardingCompleted();
                            if (redirectRoute === 'settings') {
                                window.location.replace(`/execute/wageSettingsMenu?contextId=${contextId}`);
                            } else {
                                window.location.replace(`/page/salary/create?contextId=${contextId}`);
                            }
                        }}
                        isLoading={isSubmitting}
                    >
                        <FormattedMessage id="text_get_started" />
                    </SpinnerButton>
                )}
            </span>
        </OnboardingStageFooter>
    );
}
